<template>
  <div class="academy-setup-wrapper mx-auto my-15 flex flex-col">
    <div class="academy-setup-header flex flex-col" style="gap:1rem">
      <div class="flex justify-center">
        <v-chip color="#F6F7FF" class="text-uppercase plusIndigoAccent2--text">
          <div class="flex justify-center align-center" style="gap:0.5rem;">
            <v-icon>mdi-list-status</v-icon>
            <div class="text-uppercase">{{ $t("message.onboard_getting_started") }}</div>
          </div>
        </v-chip>
      </div>
      <div>
        <div class="plusGreyDarken4--text text-center" style="font-size: 2.125rem;">
          {{ $t("message.onboard_lets_setup_your_academy") }}</div>
        <div class="text-center plusGreyDarken2--text" style="font-size: 0.875rem">
          {{ $t("message.onboard_setup_academy_explained") }}</div>
      </div>
    </div>
    <div class="academy-setup-content flex flex-col">
      <div class="flex flex-col align-center align-content-stretch" style="gap:0.5rem">
        <div class="plusGreyDarken2--text" style="font-size:0.875rem">{{ $t("message.onboard_steps_completed", {
          completed: stepsCompleted, total: Object.keys(steps).length
        }) }}</div>
        <div class="flex" style="width:100%; gap:0.25rem;">
          <div v-for="n in Object.keys(steps)" :key="n" class="rounded-xl flex-grow-1"
            :class="{ plusIndigoLighten2: steps[n], plusIndigoLighten5: !steps[n] }" style="height:0.5rem">
          </div>
        </div>
      </div>
      <div class="flex flex-col step-card-wrapper">
        <OnboardStep icon="mdi-bank"
          :title="suggested === 'COMPLETE_ACADEMY_PROFILE' || steps.COMPLETE_ACADEMY_PROFILE ? $t('message.onboard_complete_profile') : undefined"
          :description="suggested === 'COMPLETE_ACADEMY_PROFILE' || steps.COMPLETE_ACADEMY_PROFILE ? $t('message.onboard_complete_profile_explanation') : $t('message.onboard_complete_profile')"
          :suggested="isPrimaryStep('COMPLETE_ACADEMY_PROFILE')"
          :show-suggested-label="suggested === 'COMPLETE_ACADEMY_PROFILE'" :completed="steps.COMPLETE_ACADEMY_PROFILE"
          @mouseenter.native="onSetupStepEnter('COMPLETE_ACADEMY_PROFILE')"
          @mouseleave.native="onSetupStepLeave"
          @click="goToAcademySettingsWithTour" />
        <OnboardStep icon="mdi-account-multiple-plus-outline"
          :title="suggested === 'ADD_YOUR_MEMBERS' || steps.ADD_YOUR_MEMBERS ? $t('message.onboard_add_your_members') : undefined"
          :description="suggested === 'ADD_YOUR_MEMBERS' || steps.ADD_YOUR_MEMBERS ? $t('message.onboard_add_your_members_explanation') : $t('message.onboard_add_your_members')"
          :suggested="isPrimaryStep('ADD_YOUR_MEMBERS')"
          :show-suggested-label="suggested === 'ADD_YOUR_MEMBERS'" :completed="steps.ADD_YOUR_MEMBERS"
          @mouseenter.native="onSetupStepEnter('ADD_YOUR_MEMBERS')"
          @mouseleave.native="onSetupStepLeave"
          @click="goToMembersAdminWithTour" />
        <OnboardStep icon="mdi-tag-outline"
          :title="suggested === 'SETUP_PRODUCTS_MEMBERSHIPS' || steps.SETUP_PRODUCTS_MEMBERSHIPS ? $t('message.onboard_setup_products_memberships') : undefined"
          :description="suggested === 'SETUP_PRODUCTS_MEMBERSHIPS' || steps.SETUP_PRODUCTS_MEMBERSHIPS ? $t('message.onboard_setup_products_memberships_explanation') : $t('message.onboard_setup_products_memberships')"
          :suggested="isPrimaryStep('SETUP_PRODUCTS_MEMBERSHIPS')"
          :show-suggested-label="suggested === 'SETUP_PRODUCTS_MEMBERSHIPS'" :completed="steps.SETUP_PRODUCTS_MEMBERSHIPS"
          @mouseenter.native="onSetupStepEnter('SETUP_PRODUCTS_MEMBERSHIPS')"
          @mouseleave.native="onSetupStepLeave"
          @click="goToProductsMembershipsWithTour" />
        <OnboardStep icon="mdi-credit-card-outline"
          :title="suggested === 'CONNECT_PAYMENTS' || steps.CONNECT_PAYMENTS ? $t('message.onboard_connect_payments') : undefined"
          :description="suggested === 'CONNECT_PAYMENTS' || steps.CONNECT_PAYMENTS ? $t('message.onboard_connect_payments_explanation') : $t('message.onboard_connect_payments')"
          :suggested="isPrimaryStep('CONNECT_PAYMENTS')"
          :show-suggested-label="suggested === 'CONNECT_PAYMENTS'" :completed="steps.CONNECT_PAYMENTS"
          @mouseenter.native="onSetupStepEnter('CONNECT_PAYMENTS')"
          @mouseleave.native="onSetupStepLeave"
          @click="goToTransactionsAdminWithTour" />
        <OnboardStep icon="mdi-calendar-month-outline"
          :title="suggested === 'SETUP_CLASSES_SCHEDULE' || steps.SETUP_CLASSES_SCHEDULE ? $t('message.onboard_setup_classes_schedule') : undefined"
          :description="suggested === 'SETUP_CLASSES_SCHEDULE' || steps.SETUP_CLASSES_SCHEDULE ? $t('message.onboard_setup_classes_schedule_explanation') : $t('message.onboard_setup_classes_schedule')"
          :suggested="isPrimaryStep('SETUP_CLASSES_SCHEDULE')"
          :show-suggested-label="suggested === 'SETUP_CLASSES_SCHEDULE'" :completed="steps.SETUP_CLASSES_SCHEDULE"
          @mouseenter.native="onSetupStepEnter('SETUP_CLASSES_SCHEDULE')"
          @mouseleave.native="onSetupStepLeave"
          @click="goToCalendarAdminWithTour" />
      </div>
      <div class="flex flex-col" style="gap:0.5rem">
        <div class="plusGreyDarken2--text">{{ $t("message.onboard_preview_full_setup") }}</div>
        <div>
          <OnboardStep icon="mdi-play-speed" iconColor="plusTealLighten1" :title="$t('message.onboard_complete_tour')"
            :description="$t('message.onboard_complete_tour_explanation')" @click="handleStartOnboarding" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import OnboardStep from "@/components/displays/OnboardStep";
import Api2 from "@/services/Api2";

export default {
  components: {
    OnboardStep
  },
  data() {
    return {
      suggested: "COMPLETE_ACADEMY_PROFILE",
      /** @type {string | null} */
      hoveredSetupStep: null,
      /** @type {number | null} */
      hoverClearTimeoutId: null,
      steps: {
        COMPLETE_ACADEMY_PROFILE: false,
        ADD_YOUR_MEMBERS: false,
        SETUP_PRODUCTS_MEMBERSHIPS: false,
        CONNECT_PAYMENTS: false,
        SETUP_CLASSES_SCHEDULE: false
      }
    }
  },
  computed: {
    stepsCompleted: function () {
      return Object.keys(this.steps).reduce((acc, v) => {
        if (this.steps[v]) {
          acc += 1;
        }
        return acc;
      }, 0);
    }
  },
  created() {

  },
  beforeDestroy() {
    this.clearHoverClearTimeout();
    this.fetchStepStatus();
  },
  methods: {
    clearHoverClearTimeout() {
      if (this.hoverClearTimeoutId != null) {
        clearTimeout(this.hoverClearTimeoutId);
        this.hoverClearTimeoutId = null;
      }
    },
    /** @param {string} stepKey */
    onSetupStepEnter(stepKey) {
      this.clearHoverClearTimeout();
      this.hoveredSetupStep = stepKey;
    },
    onSetupStepLeave() {
      this.clearHoverClearTimeout();
      this.hoverClearTimeoutId = window.setTimeout(() => {
        this.hoverClearTimeoutId = null;
        this.hoveredSetupStep = null;
      }, 1000);
    },
    /** @param {string} stepKey */
    isPrimaryStep(stepKey) {
      if (this.hoveredSetupStep != null) {
        return this.hoveredSetupStep === stepKey;
      }
      return this.suggested === stepKey;
    },
    handleStartOnboarding() {
      this.$emit("start-onboarding");
    },
    goToAcademySettingsWithTour() {
      this.$router.push({
        name: "academysettings",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToMembersAdminWithTour() {
      this.$router.push({
        name: "members-adminplus",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToProductsMembershipsWithTour() {
      this.$router.push({
        name: "productsmemberships",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToTransactionsAdminWithTour() {
      this.$router.push({
        name: "transactions-adminplus",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToCalendarAdminWithTour() {
      this.$router.push({
        name: "calendar-adminplus",
        query: { tour: "true" }
      }).catch(() => { });
    },
    async fetchStepStatus() {
      const response = await Api2().post('trial/stepState', { "academyId": this.$store.state.managedAcademy });
      this.steps = {
        COMPLETE_ACADEMY_PROFILE: !!response.data?.academyProfile,
        ADD_YOUR_MEMBERS: !!response.data?.addedMembers,
        SETUP_PRODUCTS_MEMBERSHIPS: !!response.data?.setupProductsMemberships,
        CONNECT_PAYMENTS: !!response.data?.automaticPayments,
        SETUP_CLASSES_SCHEDULE: !!response.data?.setupSchedule
      };
      const suggestedOrder = ["COMPLETE_ACADEMY_PROFILE", "ADD_YOUR_MEMBERS",
        "SETUP_PRODUCTS_MEMBERSHIPS", "CONNECT_PAYMENTS", "SETUP_CLASSES_SCHEDULE"];
      for(const stepName of suggestedOrder) {
        if (!this.steps[stepName]) {
          this.suggested = stepName;
          break;
        }
      }
    }
  }
}
</script>

<style scoped>
.academy-setup-wrapper {
  gap: 3rem;
  max-width: 34.438rem;
}

.academy-setup-header {
  gap: 1rem;
}

.academy-setup-content {
  gap: 2rem;
}

.step-card-wrapper {
  gap: 0.75rem;
}
</style>
